import type { Scene } from "@babylonjs/core/scene";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { WebXRDefaultExperience } from "@babylonjs/core/XR/webXRDefaultExperience";
import { WebXRState } from "@babylonjs/core/XR/webXRTypes";
import { BlinkFade } from "./fade";
import type { Terrain } from "../render/terrain";

/**
 * The player rig. THIS IS THE ONLY MODULE PERMITTED TO WRITE CAMERA / RIG
 * TRANSFORM. The camera-motion audit (npm run xr:audit) fails on any camera
 * position/rotation write elsewhere. Every reposition here goes through a full
 * blackout blink — the camera never moves while visible. No smooth locomotion,
 * no dolly, no shake, ever.
 */
const EYE_HEIGHT = 1.6; // desktop fallback eye height, and the XR fallback before a pose is tracked

export class Rig {
  readonly camera: FreeCamera; // desktop fallback camera
  private readonly fade: BlinkFade;
  private xr: WebXRDefaultExperience | null = null;

  private constructor(
    private readonly scene: Scene,
    private readonly terrain: Terrain,
    camera: FreeCamera,
  ) {
    this.camera = camera;
    this.fade = new BlinkFade(scene);
  }

  static create(scene: Scene, canvas: HTMLCanvasElement, terrain: Terrain, start: { x: number; z: number }): Rig {
    const camera = new FreeCamera("devCamera", Vector3.Zero(), scene);
    camera.minZ = 0.1;
    camera.speed = 0; // no keyboard fly — locomotion is teleport only, even on desktop
    camera.attachControl(canvas, true);
    camera.keysUp = [];
    camera.keysDown = [];
    camera.keysLeft = [];
    camera.keysRight = [];
    const rig = new Rig(scene, terrain, camera);
    rig.placeCamera(start.x, start.z); // initial placement (not a runtime move)
    // Initial framing: look out over the island with a gentle downward tilt
    // (one-time setup, not runtime locomotion).
    const eye = camera.position;
    camera.setTarget(new Vector3(eye.x, eye.y - 2.5, eye.z + 14));
    return rig;
  }

  attachXR(xr: WebXRDefaultExperience): void {
    this.xr = xr;
    // Drop the player onto the spot the desktop camera was framing once we're
    // actually in the session (a pose exists, so real eye height is known).
    xr.baseExperience.onStateChangedObservable.add((state) => {
      if (state !== WebXRState.IN_XR) return;
      const p = this.camera.position;
      this.placeXR(p.x, p.z);
    });
  }

  get isBusy(): boolean {
    return this.fade.isFading;
  }

  /** Teleport to a ground point under a blink blackout. Ignored mid-fade or on invalid targets. */
  teleport(x: number, z: number): void {
    if (this.fade.isFading) return;
    if (!this.terrain.isValidTarget(x, z)) return;
    void this.fade.blink(this.scene, () => this.placeCamera(x, z));
  }

  // --- the only camera-transform writes in the codebase ----------------------
  private placeCamera(x: number, z: number): void {
    const y = this.terrain.heightAt(x, z);
    this.camera.position.set(x, y + EYE_HEIGHT, z);
    this.placeXR(x, z);
  }

  private placeXR(x: number, z: number): void {
    if (!this.xr) return;
    const xrCam = this.xr.baseExperience.camera;
    const y = this.terrain.heightAt(x, z);
    // Writing the XR camera's position makes Babylon re-offset the shared XR
    // reference space (WebXRCamera._updateReferenceSpace) — which is the SAME
    // space the controllers are tracked in, so the whole rig (head + hands)
    // moves together. We must add the player's real standing height: the write
    // sets where the *eyes* land, and `realWorldHeight` is the true head height
    // above the physical floor (read from the base reference space, so it never
    // drifts across teleports). Setting only y=terrain would pin the eyes to the
    // ground; setting y=terrain+height puts the floor under the player's feet.
    const eyeHeight = xrCam.realWorldHeight || EYE_HEIGHT;
    xrCam.position.set(x, y + eyeHeight, z);
  }
}
